import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  getProviderDashboardCriticalAlerts,
  getProviderDashboardPipeline,
  getProviderDashboardRecentPackages,
  getProviderDashboardSummary,
  getProviderIntegrationHealth,
} from '../../api/providerApi';
import { KpiCard } from '../../components/ui/KpiCard';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { buttonBase, buttonVariants, BetaTag } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import {
  IconPackage,
  IconAlert,
  IconInbox,
  IconShield,
  IconVault,
  IconPlug,
} from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import { nextActionLabel, packageStatusLabel } from './providerReviewLabels';
import { visibleIntegrationConnectors } from './visibleIntegrationConnectors';

function connectorStatusLabel(status) {
  if (!status) return '—';
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function alertHref(alert) {
  if (alert.category === 'CONNECTOR') return '/provider/integrations';
  if (alert.reference_id) return `/provider/packages/${alert.reference_id}`;
  return '/provider/exceptions';
}

function alertActionLabel(alert) {
  if (alert.category === 'CONNECTOR') return 'Inspect';
  return 'Review';
}

export function ProviderDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      setLoading(true);
      setError('');
      try {
        const [summaryResponse, pipelineResponse, alertResponse, packageResponse, healthResponse] =
          await Promise.all([
            getProviderDashboardSummary(),
            getProviderDashboardPipeline(),
            getProviderDashboardCriticalAlerts(),
            getProviderDashboardRecentPackages(),
            getProviderIntegrationHealth(),
          ]);
        if (cancelled) return;
        setSummary(summaryResponse);
        setPipeline(pipelineResponse.items || []);
        setAlerts(alertResponse.items || []);
        setPackages(packageResponse.items || []);
        setConnectors(visibleIntegrationConnectors(healthResponse.items));
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load Provider dashboard data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const kpis = useMemo(
    () => [
      {
        label: 'Received',
        value: summary?.received ?? 0,
        subline: 'Packages in this workspace',
        icon: IconPackage,
        accent: true,
      },
      {
        label: 'Blocked',
        value: summary?.blocked ?? 0,
        subline: 'Packages with open exceptions',
        icon: IconAlert,
      },
      {
        label: 'Awaiting Provider review',
        value: summary?.awaiting_provider_review ?? 0,
        subline: 'Certified, authority still pending',
        icon: IconInbox,
      },
      {
        label: 'Awaiting Custodian',
        value: summary?.awaiting_custodian ?? 0,
        subline: 'Routed candidates pending legal authority',
        icon: IconShield,
      },
      {
        label: 'Admitted',
        value: summary?.admitted ?? 0,
        subline: 'Custody accepted by Custodian',
        icon: IconVault,
      },
    ],
    [summary]
  );

  const maxPipeline = Math.max(1, ...pipeline.map((stage) => stage.count || 0));

  const packageColumns = useMemo(
    () => [
      {
        key: 'package',
        header: 'Package',
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {row.reference_number || row.package_id}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.created_at)}
            </span>
          </div>
        ),
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) =>
          row.program ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
              {row.program}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'package_status',
        header: 'Status',
        render: (row) => (
          <StatusPill value={row.package_status} label={packageStatusLabel(row.package_status)} />
        ),
      },
      {
        key: 'authority_status',
        header: 'Authority',
        render: (row) => <StatusPill value={row.authority_status} />,
      },
      {
        key: 'current_owner',
        header: 'Owner',
        render: (row) => <StatusPill value={row.current_owner} />,
      },
      {
        key: 'next_action',
        header: 'Next action',
        render: (row) => (
          <StatusPill
            value={row.next_action}
            tone={
              row.next_action === 'AWAITING_PROVIDER_REVIEW'
                ? 'warning'
                : row.next_action === 'AWAITING_CUSTODIAN' ||
                    row.next_action === 'AWAITING_ADMISSION'
                  ? 'info'
                  : 'neutral'
            }
            label={nextActionLabel(row.next_action)}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BetaTag />
          <p className="text-[13px] text-eorig-muted">
            Intake, eligibility review, and custodian handoff — not legal authority.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link
            to="/provider/intake"
            className={`${buttonBase} ${buttonVariants.ghost}`}
          >
            Open intake
          </Link>
          <Link
            to="/provider/authority-review"
            className={`${buttonBase} ${buttonVariants.gradient}`}
          >
            Authority review
          </Link>
        </div>
      </div>

      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} />
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            subline={kpi.subline}
            icon={kpi.icon}
            accent={kpi.accent}
            loading={loading}
          />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <div className="lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-eorig-ink">Recent packages</h2>
            <Link
              to="/provider/packages"
              className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              View all
            </Link>
          </div>
          <DataTable
            columns={packageColumns}
            rows={packages}
            rowKey={(row) => row.package_id}
            rowHref={(row) => `/provider/packages/${row.package_id}`}
            loading={loading}
            emptyMessage="No recent packages."
            minWidth={720}
          />
        </div>

        <section
          className="flex flex-col rounded-2xl border border-eorig-border bg-white shadow-eorig-card"
          aria-label="Critical attention"
        >
          <div className="flex items-center justify-between border-b border-eorig-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                <IconAlert className="h-4 w-4" />
              </span>
              <h2 className="text-sm font-semibold text-eorig-ink">Critical attention</h2>
            </div>
            <Link
              to="/provider/exceptions"
              className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              View all
            </Link>
          </div>
          <ul className="m-0 list-none space-y-2 p-3">
            {loading ? (
              <li className="px-1 py-6 text-center text-sm text-eorig-muted">Loading…</li>
            ) : alerts.length ? (
              alerts.map((alert, index) => (
                <li
                  key={`${alert.category}-${alert.code}-${alert.reference_id || index}`}
                  className="rounded-xl border border-eorig-border border-l-[3px] border-l-rose-400 bg-rose-50/40 p-3"
                >
                  <p className="text-sm font-semibold text-eorig-ink">
                    {alert.code || alert.category}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-eorig-muted">
                    {alert.message}
                    {alert.severity ? ` · ${alert.severity}` : ''}
                  </p>
                  <Link
                    to={alertHref(alert)}
                    className="mt-2 inline-flex items-center text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                  >
                    {alertActionLabel(alert)} →
                  </Link>
                </li>
              ))
            ) : (
              <li className="rounded-xl bg-eorig-surface px-3 py-6 text-center text-sm text-eorig-muted">
                Nothing needs attention right now.
              </li>
            )}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <h2 className="text-sm font-semibold text-eorig-ink">Pipeline by stage</h2>
        <p className="mt-1 text-[12px] text-eorig-muted">
          Package counts from intake through custodian handoff.
        </p>
        <ul className="mt-5 space-y-4">
          {loading ? (
            <li className="text-sm text-eorig-muted">Loading…</li>
          ) : pipeline.length ? (
            pipeline.map((stage) => {
              const pct = Math.round(((stage.count || 0) / maxPipeline) * 100);
              return (
                <li key={stage.stage} className="flex items-center gap-4">
                  <span className="w-36 shrink-0 text-sm text-eorig-muted">{stage.label}</span>
                  <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-eorig-surface">
                    <div
                      className="h-full rounded-full bg-eorig-gradient"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-eorig-ink">
                    {stage.count ?? 0}
                  </span>
                </li>
              );
            })
          ) : (
            <li className="text-sm text-eorig-muted">No pipeline counts returned.</li>
          )}
        </ul>
      </section>

      <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-eorig-ink">Integration health</h2>
            <p className="mt-1 text-[12px] text-eorig-muted">
              Connector status from the Provider integrations API.
            </p>
          </div>
          <Link
            to="/provider/integrations"
            className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            Open dashboard →
          </Link>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {loading ? (
            <p className="text-sm text-eorig-muted">Loading…</p>
          ) : connectors.length ? (
            connectors.map((item) => (
              <Link
                key={`${item.connector_type}-${item.name}`}
                to="/provider/integrations"
                className="flex items-center gap-3 rounded-xl border border-eorig-border p-4 transition hover:border-eorig-cyan/40"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-eorig-surface text-eorig-navy-600">
                  <IconPlug className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-eorig-ink">{item.name}</p>
                  <p className="mt-0.5 truncate text-xs text-eorig-muted">
                    {item.connector_type}
                    {item.latency_ms != null ? ` · ${item.latency_ms}ms` : ''}
                  </p>
                </div>
                <StatusPill
                  value={item.status}
                  label={connectorStatusLabel(item.status)}
                />
              </Link>
            ))
          ) : (
            <p className="text-sm text-eorig-muted">No connectors returned.</p>
          )}
        </div>
      </section>
    </div>
  );
}
